// src/firebase/athletes.ts
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { db } from "./config";
import { getFunctions, httpsCallable } from "firebase/functions";
import { withCache, invalidateCache, invalidateCachePrefix } from "../utils/cache";

const COLLECTION = "athletes";
interface AthleteRegistryResponse {
  athleteId?: string;
  slug?: string;
  docId?: string;
  created?: number;
  athletes?: ParsedAthlete[];
}
const athleteRegistryCallable = httpsCallable<Record<string, unknown>, AthleteRegistryResponse>(
  getFunctions(undefined, "europe-central2"),
  "manageOrganizationFoundation"
);

// ── Cache TTLs ────────────────────────────────────────────────────────────────
const TTL_ALL_ATHLETES    = 5  * 60 * 1000; // 5 minutes
const TTL_SINGLE_ATHLETE  = 10 * 60 * 1000; // 10 minutes

// ── Georgian → Latin transliteration ─────────────────────────────────────────

const GEO_MAP: Record<string, string> = {
  ა: "a",  ბ: "b",  გ: "g",  დ: "d",  ე: "e",  ვ: "v",  ზ: "z",
  თ: "t",  ი: "i",  კ: "k",  ლ: "l",  მ: "m",  ნ: "n",  ო: "o",
  პ: "p",  ჟ: "zh", რ: "r",  ს: "s",  ტ: "t",  უ: "u",  ფ: "f",
  ქ: "k",  ღ: "gh", ყ: "q",  შ: "sh", ჩ: "ch", ც: "ts", ძ: "dz",
  წ: "ts", ჭ: "ch", ხ: "kh", ჯ: "j",  ჰ: "h",
};

export function transliterateGeorgian(text: string): string {
  return text
    .split("")
    .map((ch) => GEO_MAP[ch] ?? (ch === " " ? " " : ch.toLowerCase()))
    .join("");
}

export function generateSlug(firstName: string, lastName: string): string {
  const combined = `${firstName} ${lastName}`;
  return transliterateGeorgian(combined)
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/** Ensures `base` is unique within `used`; appends -2, -3, … if needed.
 *  Mutates `used` so subsequent calls in the same batch stay unique. */
export function makeUniqueSlug(base: string, used: Set<string>): string {
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  let i = 2;
  while (used.has(`${base}-${i}`)) i++;
  const slug = `${base}-${i}`;
  used.add(slug);
  return slug;
}

// ── Cache invalidation helper ─────────────────────────────────────────────────

/**
 * Invalidates all athlete-related cache keys.
 * Always clears the full list and ranking snapshots.
 * If `athleteId` is provided, also clears that athlete's individual caches.
 */
function invalidateAthletesCache(athleteId?: string): void {
  invalidateCache("athletes:all");
  invalidateCache("athletes:aggregate");
  if (athleteId) {
    invalidateCache(`athletes:id:${athleteId}`);
    invalidateCache(`athleteResults:${athleteId}`);
  }
  invalidateCachePrefix("rankingSnapshot:");
}

// ── Standard CRUD ─────────────────────────────────────────────────────────────

/**
 * Returns all athletes ordered by lastName.
 * Result is cached for TTL_ALL_ATHLETES (5 min) per session.
 */
export const getAllAthletes = async () => {
  return withCache("athletes:all", TTL_ALL_ATHLETES, async () => {
    const q = query(collection(db, COLLECTION), orderBy("lastName"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  });
};

// ── Lightweight aggregate ─────────────────────────────────────────────────────

/**
 * One compact row per athlete. Mirrors the shape written by the Cloud
 * Function `maintainAthletesAggregate` (functions/index.js).
 */
export interface AthleteAggregate {
  id: string;
  athleteId: string;
  firstName: string;
  lastName: string;
  slug: string;
  dateOfBirth: string;
  club: string;
  status?: "ACTIVE" | "INACTIVE" | "ARCHIVED";
  nationality?: string;
  competitorType?: "FEDERATION_MEMBER" | "EXTERNAL_COMPETITOR";
  rankingEligible?: boolean;
}

type AthleteAggregateListener = (athlete: AthleteAggregate) => void;
const athleteAggregateListeners = new Set<AthleteAggregateListener>();

/** Keeps already-mounted search controls in sync after a local create/update. */
export function subscribeAthleteAggregateChanges(listener: AthleteAggregateListener): () => void {
  athleteAggregateListeners.add(listener);
  return () => athleteAggregateListeners.delete(listener);
}

function publishAthleteAggregateChange(athlete: AthleteAggregate): void {
  athleteAggregateListeners.forEach((listener) => listener(athlete));
}

/**
 * Reads the single `aggregates/athletes` document — one Firestore read
 * regardless of how many athletes exist — and returns the array. Built
 * for coach-facing pages (typeahead in Register, ManualRegistrationModal)
 * that only need name/id-level data, not full athlete documents.
 *
 * Cached in memory + localStorage (TTL 5 min) since the payload is pure
 * JSON (no Firestore Timestamps).
 *
 * Fallback: if the aggregate document doesn't exist yet (e.g. immediately
 * after deploy, before any athlete write has triggered the rebuild), we
 * fall back to a one-time full scan of /athletes and return the same
 * shape. The next athlete write then populates the aggregate and this
 * fallback stops running.
 */
export const getAthletesAggregate = async (): Promise<AthleteAggregate[]> => {
  return withCache<AthleteAggregate[]>(
    "athletes:aggregate",
    TTL_ALL_ATHLETES,
    async () => {
      const aggSnap = await getDoc(doc(db, "aggregates", "athletes"));
      if (aggSnap.exists()) {
        const data = aggSnap.data() as any;
        if (Array.isArray(data?.athletes)) return data.athletes as AthleteAggregate[];
      }

      // Bootstrap fallback — aggregate doc not present yet.
      const fullSnap = await getDocs(
        query(collection(db, COLLECTION), orderBy("lastName"))
      );
      return fullSnap.docs.map((d) => {
        const a = d.data() as any;
        return {
          id: d.id,
          athleteId: a.athleteId ?? "",
          firstName: a.firstName ?? "",
          lastName:  a.lastName  ?? "",
          slug:      a.slug      ?? "",
          dateOfBirth: a.dateOfBirth ?? "",
          club:      a.club      ?? "",
          status:    a.status    ?? "ACTIVE",
          nationality: a.nationality ?? a.country ?? "",
          competitorType: a.competitorType ?? "FEDERATION_MEMBER",
          rankingEligible: a.rankingEligible !== false,
        } as AthleteAggregate;
      });
    },
    { persist: true }
  );
};

/**
 * Returns a single athlete document matched by the `athleteId` field
 * (e.g. "GEO-0183"), or null if not found.
 * Result is cached for TTL_SINGLE_ATHLETE (10 min) per session.
 * This replaces the pattern of calling getAllAthletes() just to find one
 * athlete on the public AthleteProfile page.
 */
export const getAthleteByAthleteId = async (athleteId: string): Promise<any | null> => {
  return withCache(`athletes:id:${athleteId}`, TTL_SINGLE_ATHLETE, async () => {
    const q = query(
      collection(db, "publicAthletes"),
      where("athleteId", "==", athleteId)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  });
};

/**
 * Updates an athlete document.
 * Reads the document first to get the stable athleteId field, then
 * invalidates both the list cache and that athlete's individual cache.
 */
export const updateAthlete = async (docId: string, updatedData: object) => {
  // Fetch the athleteId field before updating so we can invalidate its cache.
  const snap = await getDoc(doc(db, COLLECTION, docId));
  const athleteId: string | undefined = snap.exists()
    ? (snap.data() as any).athleteId
    : undefined;

  const result = await updateDoc(doc(db, COLLECTION, docId), updatedData);
  invalidateAthletesCache(athleteId);
  return result;
};

// ── Bulk import ───────────────────────────────────────────────────────────────

export interface ParsedAthlete {
  firstName: string;
  lastName: string;
  athleteId: string;
  slug: string;
}

/**
 * Sends an explicitly confirmed admin import to the server-authoritative
 * registry allocator. IDs, aggregate publication and audit are atomic.
 *
 * @param lines        Raw lines from the textarea (may include blanks).
 * @param existingSlugs Set of slugs already in Firestore to avoid collisions.
 */
export const bulkImportAthletes = async (
  lines: string[],
  existingSlugs: Set<string>
): Promise<{ created: number; athletes: ParsedAthlete[] }> => {
  const cleanLines = lines.map((l) => l.trim()).filter((l) => l.length > 0);
  if (cleanLines.length === 0) return { created: 0, athletes: [] };
  if (cleanLines.length > 100) throw new Error("Import at most 100 athletes at a time.");

  const usedSlugs = new Set(existingSlugs);
  const requested = cleanLines.map((line) => {
    const parts = line.split(/\s+/);
    const firstName = parts[0] ?? "";
    const lastName = parts.slice(1).join(" ");
    const baseSlug = generateSlug(firstName, lastName);
    const slug = makeUniqueSlug(baseSlug, usedSlugs);
    return { firstName, lastName, slug };
  });
  const response = await athleteRegistryCallable({ action: "bulk_create_athletes", athletes: requested });
  const athletes = response.data.athletes ?? [];
  invalidateAthletesCache();
  return { created: response.data.created ?? athletes.length, athletes };
};

// ── Coach create (single athlete, from Register page) ─────────────────────────

/**
 * Creates one athlete in Firestore using the atomic ID counter.
 * Can be called by any authenticated user (coach or admin).
 * Returns the assigned athleteId, generated slug, and Firestore docId.
 */
export const createAthleteAsCoach = async (data: {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  duplicateReviewReason?: string;
  organizationId?: string;
}): Promise<{ athleteId: string; slug: string; docId: string }> => {
  const slug = generateSlug(data.firstName, data.lastName);
  const response = await athleteRegistryCallable({
    action: "create_athlete",
    firstName: data.firstName,
    lastName: data.lastName,
    dateOfBirth: data.dateOfBirth ?? "",
    duplicateReviewReason: data.duplicateReviewReason?.trim() ?? "",
    organizationId: data.organizationId ?? "",
    slug,
  });
  const { athleteId, docId } = response.data;
  if (!athleteId || !docId) throw new Error("The athlete registry returned an incomplete response.");

  // Invalidate list cache so ManageAthletes picks up the new coach-created entry.
  invalidateAthletesCache(athleteId);

  // The aggregate Cloud Function updates Firestore asynchronously. Update every
  // mounted typeahead immediately so removing and re-searching cannot encourage
  // a second record while that trigger is still running.
  publishAthleteAggregateChange({
    id: docId,
    athleteId,
    firstName: data.firstName,
    lastName: data.lastName,
    dateOfBirth: data.dateOfBirth ?? "",
    slug,
    club: "",
    status: "ACTIVE",
  });

  return { athleteId, slug: response.data.slug || slug, docId };
};

export const createAthleteAsAdmin = async (data: {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  slug?: string;
  imageUrl?: string;
  gender?: string;
  nationality?: string;
  countryCode?: string;
  competitorType?: "FEDERATION_MEMBER" | "EXTERNAL_COMPETITOR";
  rankingEligible?: boolean;
  status?: "ACTIVE" | "INACTIVE" | "ARCHIVED";
  duplicateReviewReason?: string;
}): Promise<{ athleteId: string; slug: string; docId: string }> => {
  const response = await athleteRegistryCallable({
    action: "create_athlete",
    ...data,
    slug: data.slug || generateSlug(data.firstName, data.lastName),
  });
  const { athleteId, docId } = response.data;
  if (!athleteId || !docId) throw new Error("The athlete registry returned an incomplete response.");
  const slug = response.data.slug || data.slug || generateSlug(data.firstName, data.lastName);
  invalidateAthletesCache(athleteId);
  publishAthleteAggregateChange({
    id: docId,
    athleteId,
    firstName: data.firstName,
    lastName: data.lastName,
    dateOfBirth: data.dateOfBirth ?? "",
    slug,
    club: "",
    status: data.status || "ACTIVE",
    nationality: data.nationality,
    competitorType: data.competitorType,
    rankingEligible: data.rankingEligible,
  });
  return { athleteId, slug, docId };
};

// ── Slug lookup ───────────────────────────────────────────────────────────────

export const getSlugsByAthleteIds = async (
  athleteIds: string[]
): Promise<Record<string, string>> => {
  if (athleteIds.length === 0) return {};
  const requested = new Set(athleteIds);
  const result: Record<string, string> = {};
  const athletes = await getAthletesAggregate();
  athletes.forEach((athlete) => {
    if (requested.has(athlete.athleteId) && athlete.slug) result[athlete.athleteId] = athlete.slug;
  });
  return result;
};
